import React, { useMemo, useState } from "react";
import { PivotRow, PivotTableResult, RowSeverity } from "./pivotUtils";
import { formatExactMoneyDollars } from "../Money";
import { formatSicDisplay } from "./sic";
import styles from "./PivotTable.module.css";

type SortField =
  | "group_value"
  | "location_count"
  | "total_tiv"
  | "percent_of_tiv";
type SortState = { field: SortField; order: 1 | -1 };

// Row severity → CSS class. Kept central so the (tier_1, tier_2, …) → class
// mapping lives in one place and the table just calls this.
const severityClassName = (severity: RowSeverity | null | undefined) => {
  if (severity === "tier_1") return styles.severityTier1;
  if (severity === "tier_2") return styles.severityTier2;
  if (severity === "invalid") return styles.severityInvalid;
  return "";
};

const cx = (...names: (string | false | null | undefined)[]) =>
  names.filter(Boolean).join(" ");

const formatTiv = (n: number | null | undefined) =>
  n != null
    ? formatExactMoneyDollars(n, { withRounding: true, withDollarSign: true })
    : "";

const formatPct = (n: number | null | undefined) =>
  n != null ? `${Math.round(n * 100) / 100}%` : "";

const compareNumbers = (
  a: number | null | undefined,
  b: number | null | undefined
) => (a ?? Number.NEGATIVE_INFINITY) - (b ?? Number.NEGATIVE_INFINITY);

const compareLabels = (a: string, b: string) =>
  a.localeCompare(b, undefined, { numeric: true });

function sortRows(
  rows: PivotRow[],
  sort: SortState,
  compareGroupValue: (a: string, b: string) => number
): PivotRow[] {
  const cmp = (a: PivotRow, b: PivotRow) => {
    if (sort.field === "group_value") {
      return compareGroupValue(a.group_value, b.group_value);
    }
    return compareNumbers(a[sort.field], b[sort.field]);
  };
  return [...rows].sort((a, b) => sort.order * cmp(a, b));
}

const PctCell: React.FC<{ value: number | null | undefined; max: number }> = ({
  value,
  max,
}) => {
  if (value == null) return null;
  const width = max > 0 ? Math.min(100, (100 * value) / max) : 0;
  return (
    <span className={styles.pct}>
      <span className={styles.bar}>
        <span className={styles.barFill} style={{ width: `${width}%` }} />
      </span>
      <span className={styles.pctValue}>{formatPct(value)}</span>
    </span>
  );
};

const SortHeader: React.FC<{
  field: SortField;
  sort: SortState | null;
  onSort: (field: SortField) => void;
  className: string;
  children: React.ReactNode;
}> = ({ field, sort, onSort, className, children }) => {
  const active = sort?.field === field;
  const icon = !active
    ? "pi-sort-alt"
    : sort.order === 1
      ? "pi-arrow-up"
      : "pi-arrow-down";
  return (
    <th
      className={className}
      aria-sort={
        active ? (sort.order === 1 ? "ascending" : "descending") : "none"
      }
    >
      <button
        type="button"
        className={styles.sortButton}
        onClick={() => onSort(field)}
      >
        {children}
        <i
          data-capture-hide
          className={cx(
            "pi",
            icon,
            styles.sortIcon,
            active && styles.sortIconActive
          )}
        />
      </button>
    </th>
  );
};

export const PivotTable: React.FC<{
  pivotKey: string;
  pivotData: PivotTableResult;
  label: string;
  compare?: (a: string, b: string) => number;
  tableRef: React.Ref<HTMLTableElement>;
}> = ({ pivotKey, pivotData, label, compare, tableRef }) => {
  const [sort, setSort] = useState<SortState | null>(null);
  const [expanded, setExpanded] = useState<ReadonlySet<string>>(new Set());

  const total = pivotData.total_row;
  const expandableKeys = useMemo(
    () =>
      pivotData.rows
        .filter((r) => r.sub_rows && r.sub_rows.length > 0)
        .map((r) => r.group_value),
    [pivotData.rows]
  );
  const hasSubRows = expandableKeys.length > 0;
  const allExpanded =
    hasSubRows && expandableKeys.every((key) => expanded.has(key));

  const rows = useMemo(
    () =>
      sort
        ? sortRows(pivotData.rows, sort, compare ?? compareLabels)
        : pivotData.rows,
    [pivotData.rows, sort, compare]
  );
  const maxPct = useMemo(
    () => Math.max(0, ...pivotData.rows.map((r) => r.percent_of_tiv ?? 0)),
    [pivotData.rows]
  );

  const groupValueText = (value: string) =>
    pivotKey === "sic_code" ? formatSicDisplay(value) : value;

  // First click sorts descending for numbers (biggest first) and ascending
  // for labels; the second click flips; the third clears back to the
  // pivot's natural order.
  const toggleSort = (field: SortField) => {
    setSort((prev) => {
      const first: 1 | -1 = field === "group_value" ? 1 : -1;
      if (prev?.field !== field) return { field, order: first };
      if (prev.order === first)
        return { field, order: prev.order === 1 ? -1 : 1 };
      return null;
    });
  };

  const toggleRow = (key: string) => {
    setExpanded((prev) => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });
  };

  const toggleAll = () => {
    setExpanded(allExpanded ? new Set() : new Set(expandableKeys));
  };

  return (
    <div className={styles.tableScroller}>
      <table className={styles.table} ref={tableRef}>
        <thead>
          <tr>
            {hasSubRows && (
              <th className={styles.thExpander}>
                <button
                  type="button"
                  className={styles.iconButton}
                  onClick={toggleAll}
                  title={allExpanded ? "Collapse all" : "Expand all"}
                >
                  <i
                    className={cx(
                      "pi",
                      allExpanded
                        ? "pi-angle-double-up"
                        : "pi-angle-double-down"
                    )}
                  />
                </button>
              </th>
            )}
            <SortHeader
              field="group_value"
              sort={sort}
              onSort={toggleSort}
              className={styles.thLabel}
            >
              {label}
              {hasSubRows && pivotData.sub_column_name && (
                <span className={styles.headerSub}>
                  › {pivotData.sub_column_name}
                </span>
              )}
            </SortHeader>
            <SortHeader
              field="location_count"
              sort={sort}
              onSort={toggleSort}
              className={cx(styles.thNum, styles.colCount)}
            >
              Count
            </SortHeader>
            <SortHeader
              field="total_tiv"
              sort={sort}
              onSort={toggleSort}
              className={cx(styles.thNum, styles.colTiv)}
            >
              TIV
            </SortHeader>
            <SortHeader
              field="percent_of_tiv"
              sort={sort}
              onSort={toggleSort}
              className={cx(styles.thNum, styles.colPct)}
            >
              % of TIV
            </SortHeader>
            <th className={styles.filler} />
          </tr>
        </thead>
        <tbody>
          {rows.map((row) => {
            const subRows = row.sub_rows ?? [];
            const expandable = subRows.length > 0;
            const isOpen = expandable && expanded.has(row.group_value);
            const sortedSubRows =
              isOpen && sort ? sortRows(subRows, sort, compareLabels) : subRows;
            const maxSubPct = isOpen
              ? Math.max(0, ...subRows.map((r) => r.percent_of_tiv ?? 0))
              : 0;
            return (
              <React.Fragment key={row.group_value}>
                <tr
                  className={cx(
                    styles.row,
                    expandable && styles.rowExpandable,
                    isOpen && styles.rowOpen,
                    severityClassName(row.severity)
                  )}
                  onClick={
                    expandable ? () => toggleRow(row.group_value) : undefined
                  }
                >
                  {hasSubRows && (
                    <td className={styles.tdExpander}>
                      {expandable && (
                        <i
                          className={cx(
                            "pi",
                            isOpen ? "pi-chevron-down" : "pi-chevron-right",
                            styles.chevron
                          )}
                        />
                      )}
                    </td>
                  )}
                  <td className={styles.tdLabel}>
                    {groupValueText(row.group_value)}
                  </td>
                  <td className={styles.tdNum}>{row.location_count ?? ""}</td>
                  <td className={styles.tdNum}>{formatTiv(row.total_tiv)}</td>
                  <td className={styles.tdNum}>
                    <PctCell value={row.percent_of_tiv} max={maxPct} />
                  </td>
                  <td className={styles.filler} />
                </tr>
                {isOpen &&
                  sortedSubRows.map((sub) => (
                    <tr
                      key={sub.group_value}
                      className={cx(
                        styles.subRow,
                        severityClassName(sub.severity)
                      )}
                    >
                      <td className={styles.tdExpander} />
                      <td className={styles.tdSubLabel}>{sub.group_value}</td>
                      <td className={styles.tdNum}>
                        {sub.location_count ?? ""}
                      </td>
                      <td className={styles.tdNum}>
                        {formatTiv(sub.total_tiv)}
                      </td>
                      <td
                        className={styles.tdNum}
                        title={pivotData.sub_percent_label ?? undefined}
                      >
                        <PctCell value={sub.percent_of_tiv} max={maxSubPct} />
                      </td>
                      <td className={styles.filler} />
                    </tr>
                  ))}
              </React.Fragment>
            );
          })}
        </tbody>
        <tfoot>
          <tr>
            {hasSubRows && <td className={styles.tdExpander} />}
            <td className={styles.tdLabel}>{total.group_value}</td>
            <td className={styles.tdNum}>{total.location_count ?? ""}</td>
            <td className={styles.tdNum}>{formatTiv(total.total_tiv)}</td>
            <td className={styles.tdNum}>{formatPct(total.percent_of_tiv)}</td>
            <td className={styles.filler} />
          </tr>
        </tfoot>
      </table>
    </div>
  );
};
